import { useState, useEffect, useMemo } from 'react'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useTrailerRGD } from 'hooks/trailer/useTrailerRGD'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { addHyperlinkToFirstColumn, TableData, Cell } from 'utils/data'
import { BasicPlaceholder } from 'components/placeholder'
import useCountdown from 'hooks/utils/useCountdown'
import { Container } from 'components/container/Container'
import { ErrorAlert } from 'components/error-fallback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import {
    formatDateTime,
    createRow,
    formattedHeadings,
    buildLinkedRows,
} from './helpers'
import { TRAILER_SUMMARY_PATH } from './helpers'
import { isDistrict, isGroup } from 'utils/location'

export const NonStore = () => {
    const { t, currentLocation, pathname, env, logout, session } =
        useEssentials()

    const { storeFulfillment } = TRANSLATION_KEYS

    const isGroupLocation = isGroup(currentLocation)
    const [sortedData, setSortedData] = useState<TableData>()
    const { data, isLoading, isError, error, refetch } = useTrailerRGD({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })
    const count = useCountdown(5, logout, error as Error)

    const formattedTitle = formatDateTime(t)

    const sortData = (sortDirection: 'asc' | 'desc', field: string) => {
        const fieldMapping: { [key: string]: number } = {
            unacknowledged: 1,
            'red(>36hrs)': 2,
            recentlyacknowledged: 3,
        }

        const fieldIndex = fieldMapping[field] ?? 0
        const newData = { ...sortedData, rows: sortedData?.rows ?? [] }
        const newSortedFirstRow = newData.rows[0]
        const newSortedRows = newData.rows.slice(1).sort((a, b) => {
            const aValue =
                fieldIndex > 0
                    ? Number(a[fieldIndex].value)
                    : a[fieldIndex].value
            const bValue =
                fieldIndex > 0
                    ? Number(b[fieldIndex].value)
                    : b[fieldIndex].value
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc'
                    ? aValue - bValue
                    : bValue - aValue
            } else if (
                typeof aValue === 'string' &&
                typeof bValue === 'string'
            ) {
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue)
            } else {
                return 0
            }
        })
        newData.rows = [newSortedFirstRow, ...newSortedRows]
        setSortedData(newData)
    }

    const tableHeadingsMemo = useMemo(
        () => formattedHeadings.map((h) => ({ value: t(h.value) })),
        [t],
    )

    useEffect(() => {
        if (!data) return
        const { current_location, child_locations, grandchild_locations } = data

        // Build base rows once
        const baseRows = [
            createRow(current_location),
            ...child_locations.map(createRow),
            ...(isGroupLocation ? grandchild_locations : []).map(createRow),
        ]

        const firstRow = baseRows[0]
        const otherRows = baseRows.slice(1)

        let linkedRows: Cell[][]
        if (isGroupLocation) {
            const childCount = child_locations.length
            const childRows = otherRows.slice(0, childCount)
            const grandchildRows = otherRows.slice(childCount)
            linkedRows = buildLinkedRows({
                childRows,
                grandchildRows,
                headings: tableHeadingsMemo,
                currentLocation,
                pathname,
            })
        } else {
            const singleData: TableData = {
                headings: tableHeadingsMemo,
                rows: otherRows,
            }
            const processedSingle = addHyperlinkToFirstColumn(
                singleData,
                currentLocation,
                isDistrict(currentLocation) ? TRAILER_SUMMARY_PATH : pathname,
            )
            linkedRows = processedSingle.rows
        }

        setSortedData({
            headings: tableHeadingsMemo,
            rows: [firstRow, ...linkedRows],
        })
    }, [data, currentLocation, isGroupLocation, pathname, tableHeadingsMemo])

    if (isLoading || !sortedData) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError || !data) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    const payload = {
        '': [
            {
                title: '',
                subtitle: '',
                data: { '': sortedData },
            },
        ],
    }

    const groupPayload = {
        [t(storeFulfillment.toggles.showDistricts)]: [
            {
                title: '',
                subtitle: '',
                data: {
                    '': {
                        ...sortedData,
                        rows: sortedData.rows.filter((row) => {
                            const value = row[0].value
                            return !value.includes('T')
                        }),
                    },
                },
            },
        ],
        [t(storeFulfillment.toggles.showStores)]: [
            {
                title: '',
                subtitle: '',
                data: {
                    '': {
                        ...sortedData,
                        rows: sortedData.rows.filter((row) => {
                            const value = row[0].value
                            return !value.includes('D')
                        }),
                    },
                },
            },
        ],
    }

    return (
        <Container>
            <GeneralizedComponent
                payload={isGroupLocation ? groupPayload : payload}
                sortable={true}
                widths={[100, 140, 150, 160]}
                title={formattedTitle}
                onSort={sortData}
                key={currentLocation}
                kpiName="trailer non-store"
            />
        </Container>
    )
}
